import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="bg-gradient-to-br from-primary to-primary-hover p-2 rounded-lg shadow-lg">
              <Package className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span className="font-manrope font-bold text-xl text-text-primary tracking-tight">Keeboxs</span>
          </button>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-text-muted hover:text-text-primary transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16" data-testid="terms-of-service-page">
        <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight mb-2">Terms of Service</h1>
        <p className="text-text-muted text-sm mb-10">Last updated: 09 July 2026</p>

        <div className="prose prose-gray max-w-none space-y-8 text-text-secondary text-sm leading-relaxed">
          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">1. Acceptance of Terms</h2>
            <p>By accessing or using Keeboxs, a product of Sysqua Solutions Private Limited, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you may not use our platform.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">2. Description of Service</h2>
            <p>Keeboxs is an enterprise credential and certificate management platform that enables organizations to securely store, encrypt, share, and manage sensitive credentials, SSL/TLS certificates, and related files with role-based access control.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">3. User Accounts</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>You must provide accurate and complete information during registration</li>
              <li>You are responsible for maintaining the security of your account credentials</li>
              <li>You must enable two-factor authentication (2FA) when required by your organization</li>
              <li>You must not share your account credentials with unauthorized individuals</li>
              <li>Organization administrators are responsible for managing user roles and permissions</li>
            </ul>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">4. Subscription Plans & Limits</h2>
            <p>Keeboxs offers multiple subscription tiers (Starter, Professional, Enterprise) with varying limits on credentials, certificates, users, and features. Plan limits are enforced automatically. Exceeding plan limits requires upgrading to a higher tier.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">5. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Use the platform for any illegal or unauthorized purpose</li>
              <li>Attempt to bypass security measures, rate limits, or access controls</li>
              <li>Store malicious files or content</li>
              <li>Reverse engineer, decompile, or disassemble the platform</li>
              <li>Use automated tools to scrape or access the platform beyond authorized API usage</li>
              <li>Share credentials for unauthorized systems or services</li>
            </ul>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">6. Data Ownership</h2>
            <p>You retain full ownership of all credentials, certificates, and files you upload to Keeboxs. We do not claim ownership of your data. All credential data is encrypted and accessible only to authorized users within your organization.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">7. Security Responsibilities</h2>
            <p>While we implement enterprise-grade security measures (AES-256 encryption, 2FA, brute-force protection, audit logging), you are responsible for:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Using strong, unique passwords for your account</li>
              <li>Keeping your authentication credentials confidential</li>
              <li>Reviewing audit logs for unauthorized access</li>
              <li>Promptly reporting any security incidents to our team</li>
            </ul>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">8. Service Availability</h2>
            <p>We strive to maintain high availability but do not guarantee uninterrupted service. We may perform maintenance with advance notice. Enterprise plans include SLA guarantees as specified in your agreement.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">9. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, Sysqua Solutions Private Limited shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">10. Termination</h2>
            <p>We reserve the right to suspend or terminate accounts that violate these terms. Upon termination, your access to the platform will be revoked, and your data will be retained for 30 days before permanent deletion.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">11. Changes to Terms</h2>
            <p>We may update these Terms of Service from time to time. We will notify users of material changes via email or in-app notification. Continued use of the platform after changes constitutes acceptance of the updated terms.</p>
          </section>

          <section>
            <h2 className="font-manrope font-semibold text-xl text-text-primary mb-3">12. Contact</h2>
            <p>For questions regarding these Terms of Service, contact us at:</p>
            <p className="mt-2"><strong className="text-text-primary">Email:</strong> support@keeboxs.com | support@sysqua.com</p>
            <p><strong className="text-text-primary">Website:</strong> <a href="https://sysqua.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">sysqua.com</a></p>
          </section>
        </div>
      </div>
    </div>
  );
}
